import { Injectable, inject } from '@angular/core';
import { AppStorageService } from './app-storage.service';

/**
 * MfaOnboardingService — tracks a transient "mandatory MFA enrollment in
 * progress" flag.
 *
 * Why this exists: after registration the user IS authenticated but has not
 * yet enrolled MFA. We route them to `/auth/mfa-setup`, which must be:
 *   - reachable while authenticated (so NOT behind guestGuard), and
 *   - NOT arbitrarily visitable by someone who has already enrolled and is
 *     just poking the URL.
 *
 * This flag is the "mid-onboarding" signal the route guard uses to permit the
 * setup page even for users whose MFA-required status can't be determined yet.
 * It is session-scoped (sessionStorage via AppStorageService) so it does not
 * leak across browser sessions, and is cleared once enrollment completes or is
 * abandoned via logout.
 */
@Injectable({ providedIn: 'root' })
export class MfaOnboardingService {
  private static readonly FLAG_KEY = 'mfa_onboarding_required';

  private readonly storage = inject(AppStorageService);

  /** Mark that the current session must complete MFA enrollment before proceeding. */
  begin(): void {
    this.storage.setSessionItem(MfaOnboardingService.FLAG_KEY, 'true');
  }

  /** True while the session is in the mandatory-enrollment flow. */
  isInProgress(): boolean {
    return this.storage.getSessionItem(MfaOnboardingService.FLAG_KEY) === 'true';
  }

  /** Clear the flag (enrollment completed or session ended). */
  complete(): void {
    this.storage.removeSessionItem(MfaOnboardingService.FLAG_KEY);
  }
}
